import React, { useState, useRef, useCallback, useEffect } from "react";
import { Card, CardContent, Typography, Chip, CircularProgress, Box, Stack, Tooltip, Divider, IconButton, useTheme, useMediaQuery } from "@mui/material";
import { BarChart3, Cloud, Network, Database, Target, ShieldCheck, BookOpen, ArrowRight, Clock, IndianRupee, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import SectionHeading from "../../components/ui/SectionHeading";
import { fadeUp } from "../../utils/animations";
import { getLearningPaths } from "../../api/learningPaths";
import api from "../../api/axios";

/* ------------------------------------------------------------------ */
/*  card_image_url comes back as a relative path like                  */
/*  "/media/learning-paths/cards/xyz.png". Rendered directly, that     */
/*  resolves against the React dev server's own origin (not the API    */
/*  server), so it 404s and the <img> never loads. Resolve it against  */
/*  the API's actual origin instead — same pattern as the course       */
/*  cards / course detail page.                                        */
/* ------------------------------------------------------------------ */
const API_ORIGIN = new URL(api.defaults.baseURL, window.location.origin).origin;

function resolveMediaUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path; // already absolute
  return `${API_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
}

// Fallback icon map by title keyword
const iconMap = {
  cloud: Cloud,
  security: ShieldCheck,
  network: Network,
  system: Database,
  data: BarChart3,
  seo: BarChart3,
  marketing: Target,
  project: Target,
};

// Label → gradient for placeholder cards (used only when there's no image)
const ICON_META = {
  cloud:     { gradient: "linear-gradient(135deg, #EFF6FF, #DBEAFE)" },
  security:  { gradient: "linear-gradient(135deg, #FFF1F2, #FFE4E6)" },
  network:   { gradient: "linear-gradient(135deg, #F0FDF4, #DCFCE7)" },
  system:    { gradient: "linear-gradient(135deg, #FFF7ED, #FFEDD5)" },
  data:      { gradient: "linear-gradient(135deg, #F5F3FF, #EDE9FE)" },
  seo:       { gradient: "linear-gradient(135deg, #F5F3FF, #EDE9FE)" },
  marketing: { gradient: "linear-gradient(135deg, #ECFEFF, #CFFAFE)" },
  project:   { gradient: "linear-gradient(135deg, #ECFEFF, #CFFAFE)" },
  default:   { gradient: "linear-gradient(135deg, #EFF6FF, #DBEAFE)" },
};

const LEVEL_COLOR = {
  BEGINNER: { bg: "#ECFDF5", color: "#047857" },
  INTERMEDIATE: { bg: "#FFFBEB", color: "#B45309" },
  ADVANCED: { bg: "#FEF2F2", color: "#B91C1C" },
};

// Fixed dimensions so every card matches exactly, regardless of content —
// same footprint language as the ArticleCard system used on BusinessPage.
const CARD_HEIGHT = 470;
const CARD_MEDIA_HEIGHT = 190;
const CARD_GAP = 24; // px, keep in sync with the gap set on the track below

function getIconKey(title = "") {
  return Object.keys(iconMap).find((k) => title.toLowerCase().includes(k)) || "default";
}

function formatSalary(min, max) {
  if (min == null && max == null) return null;
  const fmt = (n) => (n >= 1000 ? `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k` : n);
  if (min != null && max != null) return `₹${fmt(min)} - ₹${fmt(max)}`;
  return `₹${fmt(min ?? max)}`;
}

function parseBadges(highlightBadges) {
  if (!highlightBadges) return [];
  return String(highlightBadges)
    .split(",")
    .map((b) => b.trim())
    .filter(Boolean);
}

// Thumbnail that renders the real card_image_url when available, and
// falls back to the icon + gradient tile if there's no image, or if
// the resolved URL 404s / fails to load.
function PathThumb({ src, iconKey, isFeatured }) {
  const [failed, setFailed] = useState(!src);
  const Icon = iconMap[iconKey] || BookOpen;
  const meta = ICON_META[iconKey] ?? ICON_META.default;

  return (
    <Box
      className="card-media card-media--cover"
      sx={{
        height: CARD_MEDIA_HEIGHT,
        flexShrink: 0,
        position: "relative",
        overflow: "hidden",
        background: failed ? meta.gradient : "#F1F5FB",
        borderBottom: "1px solid rgba(15,23,42,0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {!failed && src ? (
        <Box
          component="img"
          src={src}
          alt=""
          onError={() => setFailed(true)}
          className="path-thumb-img"
          sx={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.5s ease" }}
        />
      ) : (
        <Icon size={36} color="#1d4ed8" strokeWidth={1.5} />
      )}

      {isFeatured && (
        <Chip
          icon={<Star size={12} />}
          label="Featured"
          size="small"
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            bgcolor: "#111827",
            color: "white",
            fontWeight: 600,
            fontSize: "0.65rem",
          }}
        />
      )}
    </Box>
  );
}

// CTA — a rounded rectangle (not a full pill) that sits inside its own
// breathing room at the bottom of the card, matching the reference card.
function ViewPathButton() {
  return (
    <Box
      className="path-view-btn"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        width: "100%",
        py: 1.35,
        borderRadius: "12px",
        fontWeight: 700,
        fontSize: 14.5,
        color: "white",
        background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
        boxShadow: "0 10px 22px -10px rgba(37,99,235,0.6)",
        transition: "transform 0.22s ease, box-shadow 0.22s ease, background 0.22s ease",
      }}
    >
      View path
      <ArrowRight size={16} className="path-view-arrow" style={{ transition: "transform 0.2s ease" }} />
    </Box>
  );
}

// A single learning-path card. Unchanged from the grid version — just no
// longer owns its own width (that's set by the motion.div slot in the track).
function PathCard({ path }) {
  const {
    title,
    short_tagline,
    description,
    salary_min,
    salary_max,
    duration_weeks,
    level,
    placement_support,
    is_featured,
    card_image_url,
    slug,
  } = path;

  const iconKey = getIconKey(title);
  const levelStyle = LEVEL_COLOR[level] ?? { bg: "#F1F5F9", color: "#334155" };
  const salaryLabel = formatSalary(salary_min, salary_max);
  const blurb = short_tagline || description;

  return (
    <Card
      component="a"
      href={`/learning-paths/${slug}`}
      className="path-carousel-card"
      sx={{
        height: CARD_HEIGHT,
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        borderRadius: "12px",
        border: "1px solid rgba(15,23,42,0.09)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        overflow: "hidden",
        textDecoration: "none",
        color: "inherit",
        scrollSnapAlign: "start",
        transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 16px 32px -20px rgba(15,23,42,0.22)",
          borderColor: "rgba(15,23,42,0.13)",
        },
        "&:hover .path-thumb-img": { transform: "scale(1.04)" },
        "&:hover .path-card-title": {
          color: "#2563EB",
          textDecoration: "underline",
          textDecorationColor: "rgba(37,99,235,0.35)",
          textUnderlineOffset: "3px",
        },
        "&:hover .path-view-btn": {
          transform: "translateY(-1px)",
          boxShadow: "0 14px 28px -10px rgba(37,99,235,0.75)",
          background: "linear-gradient(135deg, #2E6EF0 0%, #2050E0 100%)",
        },
        "&:hover .path-view-arrow": { transform: "translateX(3px)" },
      }}
    >
      <PathThumb
        src={resolveMediaUrl(card_image_url)}
        iconKey={iconKey}
        isFeatured={is_featured}
      />

      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <CardContent
          sx={{
            flex: 1,
            display: "grid",
            gridTemplateRows: "30px 44px 40px 1fr auto",
            rowGap: 0,
            pl: 2.25,
            pr: 2.5,
            pt: 2.25,
            pb: 2.5,
            minHeight: 0,
            minWidth: 0,
            overflow: "hidden",
            "&:last-child": { pb: 2.5 },
          }}
        >
          {/* Row 1: badges — exactly 30px, always */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "nowrap", overflow: "visible" }}>
            {level && (
              <Chip
                label={level.charAt(0) + level.slice(1).toLowerCase()}
                size="small"
                sx={{ bgcolor: levelStyle.bg, color: levelStyle.color, fontWeight: 600, fontSize: "0.65rem", flexShrink: 0 }}
              />
            )}
            {placement_support && (
              <Chip
                label="Placement support"
                size="small"
                sx={{ fontWeight: 600, fontSize: "0.65rem", bgcolor: "#EFF6FF", color: "primary.main", flexShrink: 0 }}
              />
            )}
          </Stack>

          {/* Row 2: title — exactly 44px, always, self-centered within its row */}
          <Box sx={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
            <Tooltip title={title} arrow placement="top" enterDelay={400}>
              <Typography
                className="path-card-title"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  color: "#0B1530",
                  lineHeight: "22px",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  width: "100%",
                  wordBreak: "break-word",
                  transition: "color 0.2s ease",
                }}
              >
                {title}
              </Typography>
            </Tooltip>
          </Box>

          {/* Row 3: tagline — exactly 40px, always, top-aligned right under title */}
          <Box sx={{ overflow: "hidden" }}>
            <Tooltip title={blurb || ""} arrow placement="top" enterDelay={400}>
              <Typography
                sx={{
                  fontSize: 13.5,
                  color: "#64748B",
                  lineHeight: "20px",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  width: "100%",
                  wordBreak: "break-word",
                }}
              >
                {blurb || ""}
              </Typography>
            </Tooltip>
          </Box>

          {/* Row 4: flexible spacer — grid absorbs all leftover space here, can't go negative */}
          <Box />

          {/* Row 5: footer — natural height, identical on every card since none of its content varies in line-count */}
          <Box>
            <Divider sx={{ mb: 1.5 }} />
            <Stack direction="row" spacing={2.5} sx={{ mb: 1.5 }}>
              {duration_weeks && (
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Clock size={15} color="#2563EB" />
                  </Box>
                  <Stack spacing={0}>
                    <Typography sx={{ fontSize: 10.5, color: "#94A3B8", fontWeight: 600, lineHeight: 1.3 }}>Duration</Typography>
                    <Typography sx={{ fontSize: 13, color: "#0B1530", fontWeight: 700, lineHeight: 1.3 }}>{duration_weeks} weeks</Typography>
                  </Stack>
                </Stack>
              )}
              {salaryLabel && (
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <IndianRupee size={15} color="#2563EB" />
                  </Box>
                  <Stack spacing={0}>
                    <Typography sx={{ fontSize: 10.5, color: "#94A3B8", fontWeight: 600, lineHeight: 1.3 }}>Fee range</Typography>
                    <Typography sx={{ fontSize: 13, color: "#0B1530", fontWeight: 700, lineHeight: 1.3 }}>{salaryLabel}</Typography>
                  </Stack>
                </Stack>
              )}
            </Stack>
            <ViewPathButton />
          </Box>
        </CardContent>
      </Box>
    </Card>
  );
}

export default function RoadmapSection() {
  const { data: paths = [], isLoading } = useQuery({
    queryKey: ["learning-paths"],
    queryFn: () => getLearningPaths().then((r) => r.data),
  });

  const visiblePaths = paths
    .filter((p) => p.status === "ACTIVE")
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // ---- Carousel state/logic, same pattern as CourseGrid ----
  const trackRef = useRef(null);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMd = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const cardsPerView = isXs ? 1 : isSm ? 2 : isMd ? 3 : 4;

  const [activePage, setActivePage] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  // Width so exactly `cardsPerView` cards + gaps fill the track, with no
  // partial card poking past the edge. Applied ONLY on the motion.div
  // (direct flex child of the track) — never inside PathCard itself.
  const cardWidthCss = `calc((100% - ${CARD_GAP * (cardsPerView - 1)}px) / ${cardsPerView})`;

  const totalPages = Math.max(1, Math.ceil(visiblePaths.length / cardsPerView));

  useEffect(() => {
    trackRef.current?.scrollTo({ left: 0 });
    setActivePage(0);
      setAtStart(true);
    setAtEnd(totalPages <= 1);
  }, [cardsPerView, visiblePaths.length]);

  const scrollByPage = useCallback((dir) => {
    const track = trackRef.current;
    if (!track) return;
    const firstCard = track.querySelector(".path-carousel-card");
    const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : track.clientWidth / cardsPerView;
    const step = (cardWidth + CARD_GAP) * cardsPerView;
    track.scrollBy({ left: dir * step, behavior: "smooth" });
  }, [cardsPerView]);

  const handleTrackScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    setAtStart(track.scrollLeft <= 2);
    setAtEnd(track.scrollLeft >= max - 2);
    if (track.clientWidth) {
      const page = Math.round(track.scrollLeft / track.clientWidth);
      setActivePage(Math.max(0, Math.min(page, totalPages - 1)));
    }
  }, [totalPages]);

  return (
    <Box component="section" sx={{ bgcolor: "white", pt: 1, pb: 10 }}>
      <div className="section-shell">
        <SectionHeading
          eyebrow="Career learning paths"
          title="Role-based programs with skills, salary signals, timelines, and placement support."
          copy="Each path combines certification prep, practical labs, mentor checkpoints, portfolio projects, and job-readiness workflows."
        />

        {isLoading ? (
          <Box display="flex" justifyContent="center" mt={8}>
            <CircularProgress />
          </Box>
        ) : visiblePaths.length === 0 ? (
          <Box textAlign="center" mt={8} color="text.secondary">
            <Typography>No learning paths available right now.</Typography>
          </Box>
        ) : (
          <>
            <Box
              ref={trackRef}
              onScroll={handleTrackScroll}
              className="path-carousel-track"
              sx={{
                mt: 5,
                display: "flex",
                gap: `${CARD_GAP}px`,
                overflowX: "auto",
                overflowY: "hidden",
                scrollSnapType: "x mandatory",
                scrollBehavior: "smooth",
                pb: 1.5,
                px: { xs: 0.5, md: 0 },
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {visiblePaths.map((path, i) => (
                <motion.div
                  key={path.id ?? path.slug}
                  {...fadeUp(i * 0.04)}
                  style={{
                    display: "block",
                    flex: `0 0 ${cardWidthCss}`,
                    width: cardWidthCss,
                  }}
                >
                  <PathCard path={path} />
                </motion.div>
              ))}
            </Box>

            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 3 }}>
              <IconButton
                onClick={() => scrollByPage(-1)}
                disabled={atStart}
                sx={{
                  width: 40, height: 40,
                  border: "1px solid rgba(37,99,235,0.25)",
                  color: "#2563EB",
                  bgcolor: "white",
                  "&:hover": { bgcolor: "#EFF6FF" },
                  "&.Mui-disabled": { opacity: 0.35 },
                }}
              >
                <ChevronLeft size={18} />
              </IconButton>

              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0B1530", minWidth: 64, textAlign: "center" }}>
                {String(activePage + 1).padStart(2, "0")} / {String(totalPages).padStart(2, "0")}
              </Typography>

              <IconButton
                onClick={() => scrollByPage(1)}
                disabled={atEnd}
                sx={{
                  width: 40, height: 40,
                  border: "1px solid rgba(37,99,235,0.25)",
                  color: "#2563EB",
                  bgcolor: "white",
                  "&:hover": { bgcolor: "#EFF6FF" },
                  "&.Mui-disabled": { opacity: 0.35 },
                }}
              >
                <ChevronRight size={18} />
              </IconButton>
            </Stack>
          </>
        )}
      </div>
    </Box>
  );
}